import { z } from 'zod';

/**
 * F12 venue-closure types + Zod schemas.
 *
 * Decisions: see plans/12_F12_venue_profile_self_service.md (Decision 4).
 *
 * One-off date-range closures only at MVP. Recurring patterns and per-court
 * scopes are deferred:
 *   - "every Sunday" → set Sunday closed in the weekly opening hours
 *   - "every Christmas" → rare enough that owners add a fresh closure each year
 *   - per-court closures → `sub_venue_id` reserved nullable; always NULL at F12
 *
 * Past closures are pruned nightly (`DELETE WHERE end_date < now() - 30 days`).
 */

// ─── Constants ───────────────────────────────────────────────────────────────

export const VENUE_CLOSURE_REASON_MAX_LENGTH = 200;

// ─── Raw DB row shape ────────────────────────────────────────────────────────

export type VenueClosure = {
  readonly id: string;
  readonly venueId: string;
  readonly subVenueId: string | null;
  readonly startDate: string; // YYYY-MM-DD (date)
  readonly endDate: string; // YYYY-MM-DD (date)
  readonly reason: string;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

// ─── Zod schemas ─────────────────────────────────────────────────────────────

const isoDateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');

/**
 * Create-closure payload. `subVenueId` is reserved for the future per-court
 * closure UI — always NULL in the F12 editor (Zod rejects non-null values via
 * a no-key allowance; the API never accepts a non-null sub-venue scope at
 * F12).
 *
 * `endDate >= startDate` is enforced at the API + the DB CHECK constraint.
 */
export const CreateVenueClosureInputSchema = z
  .object({
    startDate: isoDateString,
    endDate: isoDateString,
    reason: z.string().min(1).max(VENUE_CLOSURE_REASON_MAX_LENGTH),
  })
  .strict()
  .superRefine((val, ctx) => {
    if (val.endDate < val.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'endDate must be on or after startDate',
        path: ['endDate'],
      });
    }
  });
export type CreateVenueClosureInput = z.infer<typeof CreateVenueClosureInputSchema>;

/**
 * Partial update. Patching one of the date fields without the other still
 * passes Zod; the service re-validates the resulting range against the DB
 * CHECK before committing.
 */
export const UpdateVenueClosureInputSchema = z
  .object({
    startDate: isoDateString.optional(),
    endDate: isoDateString.optional(),
    reason: z.string().min(1).max(VENUE_CLOSURE_REASON_MAX_LENGTH).optional(),
  })
  .strict();
export type UpdateVenueClosureInput = z.infer<typeof UpdateVenueClosureInputSchema>;
